import { FC } from "react"
import FormContainer from "../../../components/FormContainer";
import { Helmet } from "react-helmet-async";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux/hooks";
import { selectedUser, sendCode, verifyEmail } from "../authSlice";
import "./CodeVerificationForm.scss";
import Logout from "./Logout";
import SmashSpinner from "../../../components/SmashSpinner/SmashSpinner";

const CodeVerificationForm: FC = () => {
    const digitsConfig = 6;
    const dispatch = useAppDispatch();
    const { identity, isLoading, verificationEmail } = useAppSelector(selectedUser);
    let validationCode: string;
    let refElement: HTMLInputElement;

    const isDigitKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event?.key === 'Unidentified' || !event?.key) {
            const value = refElement.value;
            const lastValue = value?.charAt(value?.length - 1);
            if (lastValue) {
                return parseInt(lastValue) >= 0 && parseInt(lastValue) <= 9;
            }
        }
        return parseInt(event?.key) >= 0 && parseInt(event?.key) <= 9;
    }

    const isBackspaceKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
        return event.key === 'Backspace';
    }

    const isCtrlV = (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
        return (event.ctrlKey || event.metaKey) && event.keyCode === 86;
    }

    const isDeleteKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
        return event.key === 'Delete';
    }
    const isNumeric = (value: string) => {
        return /[0-9]|\./.test(value);
    }

    const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (isDigitKey(event) || isCtrlV(event)) {
            if (validationCode?.toString().length === digitsConfig) {
                refElement.blur();
            }
        } else if (isDeleteKey(event) || isBackspaceKey(event)) {
            // ok
        } else {
            event.preventDefault();
        }
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (isDigitKey(event) || isCtrlV(event)) {
            if (validationCode?.toString().length === digitsConfig) {
                event.preventDefault();
            }
        } else if (isDeleteKey(event) || isBackspaceKey(event)) {
            // ok
        } else {
            event.preventDefault();
        }
    }

    const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedText = event.clipboardData.getData('text/plain');
        const pastedDigits = pastedText.split('').slice(0, digitsConfig);
        if (pastedDigits.find((digit: string) => !isNumeric(digit))) {
            event.preventDefault();
        }
    }

    const onInput = (event: React.FormEvent<HTMLInputElement>) => {
        validationCode = (event?.target as any).value;
        if (validationCode?.length > digitsConfig) {
            validationCode = validationCode.slice(0, digitsConfig);
        }
        if (validationCode?.toString().length === digitsConfig) {
            refElement.blur();
            dispatch(verifyEmail(parseInt(validationCode)));
        }
    }

    const resendCode = () => {
        dispatch(sendCode());
    }

    if (isLoading || verificationEmail.isLoading) {
        return (
            <div className="h-100 d-flex d-column align-items-center">
                <SmashSpinner />
            </div>
        );
    }

    return (
        <FormContainer>
            <Helmet>
                <title>Check your inbox</title>
                <meta name="description" content="Verify your email" />
            </Helmet>
            <h5 className="my-5"><strong>Check your inbox</strong></h5>
            <p className="fs-9">
                To start using Smash API, enter the verifcation code we just sent to <strong>{identity?.username}</strong>. This helps keep your account secure.
            </p>

            <div className="otp-container">
                <div className="otp-input-container">
                    <div className="otp-input-wrapper">
                        <div className="otp-digit-container"></div>
                        <div className="otp-digit-container"></div>
                        <div className="otp-digit-container"></div>
                        <div className="otp-digit-container"></div>
                        <div className="otp-digit-container"></div>
                        <input
                            ref={(c: HTMLInputElement) => refElement = c}
                            type="text"
                            pattern="[0-9]{6}"
                            autoComplete="nope"
                            inputMode="numeric"
                            maxLength={6}
                            onPaste={(e) => onPaste(e)}
                            onInput={(e) => onInput(e)}
                            onKeyDown={(e) => onKeyDown(e)}
                            onKeyUp={(e) => onKeyUp(e)}
                        />
                        <div className="otp-digit-container"></div>
                    </div>

                </div>
                {verificationEmail.error && <div className="error-msg text-danger fs-7"> {verificationEmail.error.message} </div>}
            </div>


            <p className="fs-8">
                No email in your inbox or spam folder? <span className="link-primary fw-bolder text-decoration-underline" onClick={resendCode}>Let’s resend it</span>.
            </p>
            <p className="fs-8">
                Wrong address? <Logout text="Log out" template="Link" /> to sign in with a different email. If you mistyped your email when signing up, create a new account.
            </p>
        </FormContainer>)
}

export default CodeVerificationForm;